import { useMemo } from 'react';
import { Chip, Typography } from '@mui/material';
import { EntityDetailDialog } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { MachineDeviceFirmware } from '../../api/types';
import { useMachineDeviceFirmware } from '../../api/queries';

/** Em-dash when null; never a raw null/blank (PORTAL_FORM_RULES §5). */
function EmDashOr({ value }: { value: string | null | undefined }) {
  return value
    ? <span>{value}</span>
    : <Typography variant="body2" component="span" color="text.secondary">—</Typography>;
}

function GatewayStatusChip({ status }: { status: string | null }) {
  if (!status) {
    return <Typography variant="body2" component="span" color="text.secondary">—</Typography>;
  }
  const color =
    status === 'online'   ? 'success' :
    status === 'starting' ? 'info' :
    status === 'silent'   ? 'warning' :
    status === 'offline'  ? 'error' :
    'default'; // dormant
  return <Chip size="small" label={status} color={color as 'success' | 'info' | 'error' | 'default' | 'warning'} />;
}

/**
 * ESP32 firmware OTA (M8) — per-HAT running-firmware detail. Read-only view of
 * the firmware a single HAT last reported (version + sha + reported-at, all
 * local-TZ), alongside the factory-bench at-register fallback and the current
 * gateway status. Opened from the IoT-devices list "Firmware" row action.
 *
 * Fetch is deferred to when the dialog opens (`enabled`) so closed rows never
 * issue a request. The IDs are never shown raw — only the QR label the caller
 * passes in identifies the HAT.
 */
export function MachineDeviceFirmwareDialog({
  open,
  machineDeviceId,
  qrLabel,
  onClose,
}: {
  open: boolean;
  machineDeviceId: string | null;
  qrLabel: string | null;
  onClose: () => void;
}) {
  const q = useMachineDeviceFirmware(machineDeviceId, open);

  const fields = useMemo<DetailRow<MachineDeviceFirmware>[]>(() => [
    { label: 'Running firmware', render: (r) => <EmDashOr value={r.firmwareVersion} /> },
    {
      label: 'Firmware SHA',
      render: (r) => r.firmwareSha
        ? <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{r.firmwareSha}</code>
        : <EmDashOr value={null} />,
    },
    { label: 'Reported at', render: (r) => fmtDateTime(r.firmwareVersionReportedAt) },
    {
      label: 'Firmware at register',
      separator: true,
      render: (r) => <EmDashOr value={r.firmwareVersionAtRegister} />,
    },
    // Gateway version reporting (0397) — the running Route.MachineGateway
    // container version, alongside the ESP32 firmware above.
    {
      label: 'Gateway version',
      separator: true,
      render: (r) => <EmDashOr value={r.gatewayVersion} />,
    },
    { label: 'Gateway reported at', render: (r) => fmtDateTime(r.gatewayVersionReportedAt) },
    { label: 'Gateway status', render: (r) => <GatewayStatusChip status={r.gatewayStatus} /> },
  ], []);

  return (
    <EntityDetailDialog<MachineDeviceFirmware>
      open={open}
      title={`HAT firmware · ${fmtOrEmDash(qrLabel)}`}
      isLoading={q.isLoading}
      error={q.error ? { message: (q.error as { message?: string }).message } : null}
      data={q.data}
      fields={fields}
      onClose={onClose}
    />
  );
}
